/**
 * Base Grid Plugin Class
 *
 * All plugins extend this abstract class.
 * Plugins are instantiated per-grid and manage their own state.
 */

// Injected by Vite at build time from package.json (same as grid.ts)
declare const __GRID_VERSION__: string;

import type { HeaderContentDefinition, ToolPanelDefinition } from '../../plugins/shell/types';
import { DEFAULT_GRID_ICONS } from '../defaults';
import {
  type DiagnosticCode,
  PLUGIN_ALIAS_CONFIG_CONFLICT,
  formatDiagnostic,
  gridPrefix,
} from '../internal/diagnostics';
import { setSanitizedHTML } from '../internal/sanitize';
import type {
  ColumnConfig,
  ColumnState,
  GridConfig,
  GridIcons,
  GridPlugin,
  IconValue,
  PluginNameMap,
  Translate,
} from '../types';

// Re-export shared plugin types for convenience
export type {
  AfterCellRenderContext,
  AfterRowRenderContext,
  CellClickEvent,
  CellCoords,
  CellEditor,
  CellMouseEvent,
  CellRenderer,
  ContextMenuItem,
  ContextMenuParams,
  GridElementRef,
  HeaderClickEvent,
  HeaderRenderer,
  KeyboardModifiers,
  PluginCellRenderContext,
  PluginQuery,
  RowClickEvent,
  ScrollEvent,
} from './types';

import type {
  AfterCellRenderContext,
  AfterRowRenderContext,
  CellClickEvent,
  CellEditor,
  CellMouseEvent,
  CellRenderer,
  GridElementRef,
  HeaderClickEvent,
  HeaderRenderer,
  PluginQuery,
  RowClickEvent,
  ScrollEvent,
} from './types';

/**
 * Grid element interface for plugins.
 * Extends GridElementRef with plugin-specific methods.
 * Note: effectiveConfig is already available from GridElementRef.
 */
export interface GridElement extends GridElementRef {
  getPlugin<T extends GridPlugin>(PluginClass: new (...args: any[]) => T): T | undefined;
  getPluginByName<K extends string>(
    name: K,
  ): (K extends keyof PluginNameMap ? PluginNameMap[K] : BaseGridPlugin) | undefined;
  /**
   * Get a plugin's state by plugin name.
   * This is a loose-coupling method for plugins to access other plugins' state
   * without importing the plugin class directly.
   * @internal Plugin API
   */
  getPluginState?(name: string): unknown;

  // Focus Management
  /** Register an external DOM element as a logical focus container. @internal Plugin API */
  registerExternalFocusContainer?(el: Element): void;
  /** Unregister a previously registered external focus container. @internal Plugin API */
  unregisterExternalFocusContainer?(el: Element): void;
  /** Check whether focus is logically inside this grid (own DOM + external containers). @internal Plugin API */
  containsFocus?(node?: Node | null): boolean;
}

/**
 * Header render context for plugin header renderers.
 */
export interface PluginHeaderRenderContext {
  /** Column configuration */
  column: ColumnConfig;
  /** Column index */
  colIndex: number;
}

// ============================================================================
// Plugin Dependency Types
// ============================================================================

/**
 * Declares a dependency on another plugin.
 *
 * @category Plugin Development
 * @example
 * ```typescript
 * export class UndoRedoPlugin extends BaseGridPlugin {
 *   static readonly dependencies: PluginDependency[] = [
 *     { name: 'editing', required: true },
 *   ];
 * }
 * ```
 * @since 0.4.1
 */
export interface PluginDependency {
  /**
   * The name of the required plugin (matches the plugin's `name` property).
   * Use string names for loose coupling - avoids static imports.
   */
  name: string;

  /**
   * Whether this dependency is required (hard) or optional (soft).
   * - `true`: Plugin cannot function without it. Throws error if missing.
   * - `false`: Plugin works with reduced functionality. Silent if missing
   *   (set {@link severity} to `'warn'` or `'info'` to opt into a message).
   * @default true
   */
  required?: boolean;

  /**
   * Explicit severity when this dependency is missing. Overrides the default
   * derived from {@link required}.
   * - `'error'`: throw (halts grid setup). Default when `required` is not `false`.
   * - `'warn'`: log a console warning and continue (development only).
   * - `'info'`: log a verbose console.debug message and continue (development only).
   *
   * When omitted, a missing hard dependency throws and a missing soft
   * dependency is silent — preserving backward-compatible behavior.
   * @since 2.16.0
   */
  severity?: 'error' | 'warn' | 'info';

  /**
   * Optional predicate that gates whether this dependency applies, evaluated
   * against the depending plugin's own resolved configuration (defaults merged
   * with user config). Return `false` to skip the dependency entirely.
   *
   * Lets a plugin require/recommend another plugin only under certain config
   * — e.g. a plugin that needs a tool-panel host only when its tool panel is
   * enabled. When omitted, the dependency always applies.
   * @example when: (cfg: PivotConfig) => cfg.showToolPanel === true
   * @since 2.16.0
   */
  when?: (pluginConfig: unknown) => boolean;

  /**
   * Factory for a default instance of the dependency. When the dependency is
   * missing, the grid attaches the instance this returns (once per grid,
   * before the depending plugin) instead of reporting an error, so consumers
   * never have to add it themselves. An explicitly configured instance always
   * wins.
   *
   * Import the dependency's plugin module statically so it ships with the
   * depending plugin's module graph — never with `import()`.
   *
   * @example provide: () => new HierarchyPlugin()
   * @since 3.9.0
   */
  provide?: () => BaseGridPlugin;

  /**
   * Human-readable reason for this dependency.
   * Shown in error/info messages to help users understand why.
   * @example "UndoRedoPlugin needs EditingPlugin to track cell edits"
   */
  reason?: string;
}

/**
 * Declares an incompatibility between plugins.
 * When both plugins are loaded, a warning is logged to help users understand the conflict.
 *
 * @category Plugin Development
 */
export interface PluginIncompatibility {
  /**
   * The name of the incompatible plugin (matches the plugin's `name` property).
   */
  name: string;

  /**
   * Human-readable reason for the incompatibility.
   * Should explain why the plugins conflict and any workarounds.
   * @example "Both transform the entire row model in different ways"
   */
  reason: string;
}

// ============================================================================
// Plugin Query & Event Definitions
// ============================================================================

/**
 * Defines a query that a plugin can handle.
 * Other plugins or the grid can send this query type to retrieve data.
 *
 * @category Plugin Development
 *
 * @example
 * ```typescript
 * // In manifest
 * queries: [
 *   {
 *     type: 'canMoveColumn',
 *     description: 'Check if a column can be moved/reordered',
 *   },
 * ]
 *
 * // In plugin class
 * handleQuery(query: PluginQuery): unknown {
 *   if (query.type === 'canMoveColumn') {
 *     return this.canMoveColumn(query.context);
 *   }
 * }
 * ```
 * @since 1.8.0
 */
export interface QueryDefinition {
  /**
   * The query type identifier (e.g., 'canMoveColumn', 'getContextMenuItems').
   * Should be unique across all plugins.
   */
  type: string;

  /**
   * Human-readable description of what the query does.
   */
  description?: string;
}

/**
 * Defines an event that a plugin can emit.
 * Other plugins can subscribe to these events via the Event Bus.
 *
 * @category Plugin Development
 *
 * @example
 * ```typescript
 * // In manifest
 * events: [
 *   {
 *     type: 'filter-change',
 *     description: 'Emitted when filter criteria change',
 *   },
 * ]
 *
 * // In plugin class - emit
 * this.emitPluginEvent('filter-change', { field: 'name', value: 'Alice' });
 *
 * // In another plugin - subscribe
 * this.on('filter-change', (detail) => console.log('Filter changed:', detail));
 * ```
 * @since 1.8.0
 */
export interface EventDefinition {
  /**
   * The event type identifier (e.g., 'filter-change', 'selection-change').
   * Used when emitting and subscribing to events.
   */
  type: string;

  /**
   * Human-readable description of what the event represents.
   */
  description?: string;

  /**
   * Whether this event is cancelable via `event.preventDefault()`.
   * @default false
   */
  cancelable?: boolean;
}

// ============================================================================
// Plugin Manifest Types
// ============================================================================

/**
 * Defines a property that a plugin "owns" - used for configuration validation.
 * When this property is used without the owning plugin loaded, an error is thrown.
 *
 * @category Plugin Development
 */
export interface PluginPropertyDefinition {
  /** The property name on column or grid config */
  property: string;
  /** Whether this is a column-level or config-level property */
  level: 'column' | 'config';
  /** Human-readable description for error messages (e.g., 'the "editable" column property') */
  description: string;
  /** Import path hint for error messages (e.g., "import { EditingPlugin } from '@toolbox-web/grid/plugins/editing';") */
  importHint?: string;
  /** Custom check for whether property is considered "used" (default: truthy value check) */
  isUsed?: (value: unknown) => boolean;
}

/**
 * A configuration validation rule for detecting invalid/conflicting settings.
 * Plugins declare rules in their manifest; the validator executes them during grid initialization.
 *
 * @category Plugin Development
 * @template TConfig - The plugin's configuration type
 */
export interface PluginConfigRule<TConfig = unknown> {
  /** Rule identifier for debugging (e.g., 'selection/range-dblclick') */
  id: string;
  /** Severity: 'error' throws, 'warn' logs warning */
  severity: 'error' | 'warn';
  /** Human-readable message shown when rule is violated */
  message: string;
  /** Predicate returning true if the rule is VIOLATED (i.e., config is invalid) */
  check: (pluginConfig: TConfig) => boolean;
}

/**
 * Hook names that can have execution priority configured.
 *
 * @category Plugin Development
 */
export type HookName =
  | 'processColumns'
  | 'processConfig'
  | 'processRows'
  | 'afterStructuralRender'
  | 'afterRender'
  | 'afterCellRender'
  | 'afterRowRender'
  | 'onCellClick'
  | 'onCellMouseDown'
  | 'onCellMouseMove'
  | 'onCellMouseUp'
  | 'onHeaderClick'
  | 'onKeyDown'
  | 'onRowClick'
  | 'onScroll'
  | 'onScrollRender'
  | 'renderRow';

/**
 * Static metadata about a plugin's capabilities and requirements.
 * Declared as a static property on plugin classes.
 *
 * @category Plugin Development
 * @template TConfig - The plugin's configuration type
 *
 * @example
 * ```typescript
 * export class MyPlugin extends BaseGridPlugin<MyConfig> {
 *   static override readonly manifest: PluginManifest<MyConfig> = {
 *     ownedProperties: [
 *       { property: 'myProp', level: 'column', description: 'the "myProp" column property' },
 *     ],
 *     configRules: [
 *       { id: 'my-plugin/invalid-combo', severity: 'warn', message: '...', check: (c) => c.a && c.b },
 *     ],
 *   };
 *   readonly name = 'myPlugin';
 * }
 * ```
 * @since 1.1.0
 */
export interface PluginManifest<TConfig = unknown> {
  /**
   * Properties this plugin owns - validated by validate-config.ts.
   * If a user uses one of these properties without loading the plugin, an error is thrown.
   */
  ownedProperties?: PluginPropertyDefinition[];

  /**
   * Hook execution priority (higher = later, default 0).
   * Use negative values to run earlier, positive to run later.
   */
  hookPriority?: Partial<Record<HookName, number>>;

  /**
   * Configuration validation rules - checked during grid initialization.
   * Rules with severity 'error' throw, 'warn' logs to console.
   */
  configRules?: PluginConfigRule<TConfig>[];

  /**
   * Plugins that are incompatible with this plugin.
   * When both plugins are loaded together, a warning is shown.
   *
   * @example
   * ```typescript
   * incompatibleWith: [
   *   { name: 'tree', reason: 'Both transform the entire row model in different ways' },
   * ],
   * ```
   */
  incompatibleWith?: PluginIncompatibility[];

  /**
   * Queries this plugin can handle.
   * Declares what query types this plugin responds to via `handleQuery()`.
   * This replaces the centralized PLUGIN_QUERIES approach with manifest-declared queries.
   *
   * @example
   * ```typescript
   * queries: [
   *   { type: 'canMoveColumn', description: 'Check if a column can be moved' },
   * ],
   * ```
   */
  queries?: QueryDefinition[];

  /**
   * Events this plugin can emit.
   * Declares what event types other plugins can subscribe to via `on()`.
   *
   * @example
   * ```typescript
   * events: [
   *   { type: 'filter-change', description: 'Emitted when filter criteria change' },
   * ],
   * ```
   */
  events?: EventDefinition[];

  /**
   * Whether this plugin's `processRows` hook injects or removes rows
   * (group headers, tree nodes, pivot aggregates, placeholders, etc.).
   *
   * When true, the core sorting module delegates to the render scheduler
   * instead of sorting `_rows` in-place, because `_rows` may contain
   * plugin-generated marker objects that would be corrupted by a direct sort.
   *
   * Plugins that only **filter** or **reorder** existing rows (e.g. Filtering,
   * MultiSort) should leave this unset or set it to `false`.
   */
  modifiesRowStructure?: boolean;
}

/** Convert camelCase icon key to kebab-case data-icon attribute value. */
export function toIconAttr(key: string): string {
  return key.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * Abstract base class for all grid plugins.
 *
 * @category Plugin Development
 * @template TConfig - Configuration type for the plugin
 * @since 0.1.1
 */
export abstract class BaseGridPlugin<TConfig = unknown> implements GridPlugin {
  /**
   * Plugin dependencies - declare other plugins this one requires.
   *
   * Dependencies are validated when the plugin is attached.
   * Required dependencies throw an error if missing. Optional dependencies are
   * silent by default; set a {@link PluginDependency.severity} of `'warn'` or
   * `'info'` to opt into a development-only log.
   *
   * @example
   * ```typescript
   * static readonly dependencies: PluginDependency[] = [
   *   { name: 'editing', required: true, reason: 'Tracks cell edits for undo/redo' },
   *   { name: 'selection', required: false, reason: 'Enables selection-based undo' },
   * ];
   * ```
   */
  static readonly dependencies?: PluginDependency[];

  /**
   * Plugin manifest - declares owned properties, config rules, and hook priorities.
   *
   * This is read by the configuration validator to:
   * - Validate that required plugins are loaded when their properties are used
   * - Execute configRules to detect invalid/conflicting settings
   * - Order hook execution based on priority
   *
   * @example
   * ```typescript
   * static override readonly manifest: PluginManifest<MyConfig> = {
   *   ownedProperties: [
   *     { property: 'myProp', level: 'column', description: 'the "myProp" column property' },
   *   ],
   *   configRules: [
   *     { id: 'myPlugin/conflict', severity: 'warn', message: '...', check: (c) => c.a && c.b },
   *   ],
   * };
   * ```
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static readonly manifest?: PluginManifest<any>;

  /** Unique plugin identifier (derived from class name by default) */
  abstract readonly name: string;

  /**
   * Alternative names for backward compatibility.
   * `getPluginByName()` matches against both `name` and `aliases`.
   * @internal
   */
  readonly aliases?: readonly string[];

  /**
   * Plugin version - defaults to grid version for built-in plugins.
   * Third-party plugins can override with their own semver.
   */
  readonly version: string = typeof __GRID_VERSION__ !== 'undefined' ? __GRID_VERSION__ : 'dev';

  /** CSS styles to inject via `document.adoptedStyleSheets` */
  readonly styles?: string;

  /** Custom cell renderers keyed by type name */
  readonly cellRenderers?: Record<string, CellRenderer>;

  /** Custom header renderers keyed by type name */
  readonly headerRenderers?: Record<string, HeaderRenderer>;

  /** Custom cell editors keyed by type name */
  readonly cellEditors?: Record<string, CellEditor>;

  /** The grid instance this plugin is attached to */
  protected grid!: GridElement;

  /** Plugin configuration - merged with defaults in attach() */
  protected config!: TConfig;

  /** User-provided configuration from constructor */
  protected readonly userConfig: Partial<TConfig>;

  /**
   * Plugin-level AbortController for event listener cleanup.
   * Created fresh in attach(), aborted in detach().
   * This ensures event listeners are properly cleaned up when plugins are re-attached.
   */
  #abortController?: AbortController;

  /**
   * Default configuration - subclasses should override this getter.
   * Note: This must be a getter (not property initializer) for proper inheritance
   * since property initializers run after parent constructor.
   */
  protected get defaultConfig(): Partial<TConfig> {
    return {};
  }

  constructor(config: Partial<TConfig> = {}) {
    this.userConfig = config;
  }

  /**
   * Effective configuration (defaults merged with user config), resolved
   * eagerly so it is available **before** {@link attach} runs. Dependency
   * validation happens before `attach()` merges {@link config}, so config
   * -conditional dependency `when` predicates evaluate against this getter.
   *
   * While attached, returns the already-merged {@link config}; otherwise
   * recomputes `defaultConfig` + `userConfig` fresh. `config` is intentionally
   * **not** trusted when detached because {@link detach} does not clear it — a
   * re-validated plugin would otherwise see stale config from a prior
   * attachment. The {@link attach}-managed abort controller is the
   * attached/detached signal.
   * @internal Plugin infrastructure (used by `validatePluginDependencies`).
   */
  get resolvedConfig(): TConfig {
    return (this.#abortController ? this.config : { ...this.defaultConfig, ...this.userConfig }) as TConfig;
  }

  /**
   * Merge user-supplied configuration from other plugin instances into this
   * plugin's `userConfig`. Used by `PluginManager.attachAll()`'s alias-collapse
   * pre-pass: when a consumer instantiates the same plugin more than once (or
   * under an alias name), only one instance is attached, and the other
   * instances' configs are folded in via this method.
   *
   * Merge rules (shallow):
   *
   * - Same key, **same value** → silent.
   * - Same key, **different values** (incl. function vs function) → throws
   *   a diagnostic naming the conflicting key. Conflicting callbacks
   *   (`canDrag`, `canDrop`, etc.) cannot be reconciled automatically.
   * - **Disjoint keys** → merged cleanly.
   *
   * Subclasses may override to implement custom deep-merge logic, but should
   * call `super.mergeConfigsFrom(others)` to inherit the conflict-detection.
   *
   * @internal Plugin infrastructure (used by `PluginManager.attachAll`).
   * @throws Error with diagnostic code `TBW025` when configs conflict.
   */
  mergeConfigsFrom(others: readonly BaseGridPlugin<TConfig>[]): void {
    if (others.length === 0) return;
    const merged = { ...this.userConfig } as Record<string, unknown>;
    const sources: Record<string, BaseGridPlugin<TConfig>> = {};
    for (const key of Object.keys(merged)) sources[key] = this;
    for (const other of others) {
      const otherConfig = other.userConfig as Record<string, unknown>;
      for (const [key, value] of Object.entries(otherConfig)) {
        if (value === undefined) continue;
        if (!(key in merged)) {
          merged[key] = value;
          sources[key] = other;
          continue;
        }
        if (merged[key] === value) continue;
        // Conflict
        const ownerA = sources[key]?.constructor.name ?? this.constructor.name;
        const ownerB = other.constructor.name;
        const code = formatDiagnostic(
          PLUGIN_ALIAS_CONFIG_CONFLICT,
          `Cannot merge plugin configs for "${this.name}": conflicting value for "${key}" supplied by both ${ownerA} and ${ownerB}. Pass the option on a single instance, or remove the duplicate.`,
          undefined,
          this.name,
        );
        throw new Error(code);
      }
    }
    // Mutate userConfig in place so re-attach picks it up via attach().
    // `userConfig` is declared `readonly` to prevent reassigning the binding,
    // but the object's own properties are intentionally mutable for this
    // alias-collapse pre-pass. A single cast to a record is enough — no
    // `as unknown as` double-cast needed.
    Object.assign(this.userConfig as Record<string, unknown>, merged);
  }

  /**
   * Replace this plugin's `userConfig` with a freshly-resolved sibling
   * instance's config. Used by the grid's per-grid feature-instance gate
   * (`#initializePlugins`): when a feature is re-resolved across a config
   * change, the **existing** instance is reused — so its accumulated state
   * survives — but it must adopt the latest `gridConfig.features.<name>`
   * value. Unlike {@link mergeConfigsFrom}, this is last-write-wins with no
   * conflict detection: the re-resolved instance is authoritative.
   *
   * `attach()` recomputes {@link config} from `userConfig`, so updating
   * `userConfig` here is enough for the reused instance to pick up the new
   * config on its next attach during the same `#initializePlugins` pass.
   *
   * @internal Plugin infrastructure (used by the grid feature gate).
   */
  refreshUserConfigFrom(other: BaseGridPlugin<TConfig>): void {
    const target = this.userConfig as Record<string, unknown>;
    // Snapshot the source BEFORE clearing `target`. A feature factory may
    // store the consumer's config object by reference (e.g. `new
    // ContextMenuPlugin(config)`), and a consumer that passes the *same* config
    // object across re-resolutions (common when `gridConfig` is a recomputed
    // Angular `computed()`) makes `this.userConfig === other.userConfig`.
    // Without this snapshot, the `delete` loop below would empty that shared
    // object first, then `Object.assign` would copy back nothing — silently
    // wiping the config and falling back to plugin defaults. (#contextMenu)
    const source = { ...(other.userConfig as Record<string, unknown>) };
    for (const key of Object.keys(target)) delete target[key];
    Object.assign(target, source);
  }

  /**
   * Called when the plugin is attached to a grid.
   * Override to set up event listeners, initialize state, etc.
   *
   * @example
   * ```ts
   * attach(grid: GridElement): void {
   *   super.attach(grid);
   *   // Set up document-level listeners with auto-cleanup
   *   document.addEventListener('keydown', this.handleEscape, {
   *     signal: this.disconnectSignal
   *   });
   * }
   * ```
   */
  attach(grid: GridElement): void {
    // Abort any previous abort controller (in case of re-attach without detach)
    this.#abortController?.abort();
    // Create fresh abort controller for this attachment
    this.#abortController = new AbortController();

    this.grid = grid;
    // Merge config here (after subclass construction is complete)
    this.config = { ...this.defaultConfig, ...this.userConfig } as TConfig;
  }

  /**
   * Called when the plugin is detached from a grid.
   * Override to clean up event listeners, timers, etc.
   *
   * @example
   * ```ts
   * detach(): void {
   *   // Clean up any state not handled by disconnectSignal
   *   this.selectedRows.clear();
   *   this.cache = null;
   * }
   * ```
   */
  detach(): void {
    // Abort the plugin's abort controller to clean up all event listeners
    // registered with { signal: this.disconnectSignal }
    this.__releaseDisconnectSignal();
    // Override in subclass for additional cleanup
  }

  /**
   * Abort {@link disconnectSignal} (idempotent). The plugin manager calls this after
   * every `detach()` so listeners bound to the signal are released even when an
   * override does not call `super.detach()`.
   * @internal
   */
  __releaseDisconnectSignal(): void {
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /**
   * Called when another plugin is attached to the same grid.
   * Use for inter-plugin coordination, e.g., to integrate with new plugins.
   *
   * @param name - The name of the plugin that was attached
   * @param plugin - The plugin instance (for direct access if needed)
   *
   * @example
   * ```ts
   * onPluginAttached(name: string, plugin: BaseGridPlugin): void {
   *   if (name === 'selection') {
   *     // Integrate with selection plugin
   *     this.selectionPlugin = plugin as SelectionPlugin;
   *   }
   * }
   * ```
   */
  onPluginAttached?(name: string, plugin: BaseGridPlugin): void;

  /**
   * Called when another plugin is detached from the same grid.
   * Use to clean up inter-plugin references.
   *
   * @param name - The name of the plugin that was detached
   *
   * @example
   * ```ts
   * onPluginDetached(name: string): void {
   *   if (name === 'selection') {
   *     this.selectionPlugin = undefined;
   *   }
   * }
   * ```
   */
  onPluginDetached?(name: string): void;

  /**
   * Get another plugin instance from the same grid.
   * Use for inter-plugin communication.
   *
   * **Prefer {@link BaseGridPlugin.grid grid.getPluginByName()}** when you don't need the class import.
   *
   * @example
   * ```ts
   * // Preferred: by name
   * const selection = this.grid?.getPluginByName('selection');
   *
   * // Alternative: by class
   * const selection = this.getPlugin(SelectionPlugin);
   * if (selection) {
   *   const selectedRows = selection.getSelectedRows();
   * }
   * ```
   */
  protected getPlugin<T extends BaseGridPlugin>(PluginClass: new (...args: any[]) => T): T | undefined {
    return this.grid?.getPlugin(PluginClass);
  }

  /**
   * Emit a custom event from the grid.
   */
  protected emit<T>(eventName: string, detail: T): void {
    this.grid?.dispatchEvent?.(new CustomEvent(eventName, { detail, bubbles: true }));
  }

  /**
   * Emit a cancelable custom event from the grid.
   * @returns `true` if the event was cancelled (preventDefault called), `false` otherwise
   */
  protected emitCancelable<T>(eventName: string, detail: T): boolean {
    const event = new CustomEvent(eventName, { detail, bubbles: true, cancelable: true });
    this.grid?.dispatchEvent?.(event);
    return event.defaultPrevented;
  }

  // =========================================================================
  // Event Bus - Plugin-to-Plugin Communication
  // =========================================================================

  /**
   * Subscribe to an event from another plugin.
   * The subscription is automatically cleaned up when this plugin is detached.
   *
   * @category Plugin Development
   * @param eventType - The event type to listen for (e.g., 'filter-change')
   * @param callback - The callback to invoke when the event is emitted
   *
   * @example
   * ```typescript
   * // In attach() or other initialization
   * this.on('filter-change', (detail) => {
   *   console.log('Filter changed:', detail);
   * });
   * ```
   */
  protected on<T = unknown>(eventType: string, callback: (detail: T) => void): void {
    this.grid?._pluginManager?.subscribe(this, eventType, callback as (detail: unknown) => void);
  }

  /**
   * Unsubscribe from a plugin event.
   *
   * @category Plugin Development
   * @param eventType - The event type to stop listening for
   *
   * @example
   * ```typescript
   * this.off('filter-change');
   * ```
   */
  protected off(eventType: string): void {
    this.grid?._pluginManager?.unsubscribe(this, eventType);
  }

  /**
   * Emit an event to other plugins via the Event Bus.
   * This is for inter-plugin communication only; it does NOT dispatch DOM events.
   * Use `emit()` to dispatch DOM events that external consumers can listen to.
   *
   * @category Plugin Development
   * @param eventType - The event type to emit (should be declared in manifest.events)
   * @param detail - The event payload
   *
   * @example
   * ```typescript
   * // Emit to other plugins (not DOM)
   * this.emitPluginEvent('filter-change', { field: 'name', value: 'Alice' });
   *
   * // For DOM events that consumers can addEventListener to:
   * this.emit('filter-change', { field: 'name', value: 'Alice' });
   * ```
   */
  protected emitPluginEvent<T>(eventType: string, detail: T): void {
    this.grid?._pluginManager?.emitPluginEvent(eventType, detail);
  }

  /**
   * Emit an event to **both** the plugin Event Bus (for inter-plugin communication)
   * **and** the DOM (for external consumers via `addEventListener`).
   *
   * Use this when a state change is relevant to both other plugins and external
   * consumers. For example, `sort-change` needs to invalidate Selection (plugin bus)
   * and notify the host application (DOM event).
   *
   * @category Plugin Development
   * @param eventType - The event type to broadcast
   * @param detail - The event payload
   *
   * @example
   * ```typescript
   * // Notify both plugins and consumers of a sort change
   * this.broadcast('sort-change', { sortModel: [...this.sortModel] });
   * ```
   */
  protected broadcast<T>(eventType: string, detail: T): void {
    this.emitPluginEvent(eventType, detail);
    this.emit(eventType, detail);
  }

  /**
   * Run a yes/no plugin query and reduce the responses to a single boolean.
   *
   * `grid.query()` always returns an **array** of responses (empty when no
   * plugin handles the query), and `[]` is truthy in JavaScript — so testing
   * the raw result directly (`if (grid.query(...))`) is always `true` and
   * silently takes the "some plugin said yes" branch even when nothing
   * answered. Always route boolean queries through this helper.
   *
   * @category Plugin Development
   * @param type - The query type to dispatch
   * @param context - Optional query context passed to the handlers
   * @returns `true` only when at least one plugin answered truthily
   *
   * @example
   * ```typescript
   * if (this.queryBoolean('datasource:is-active')) {
   *   // A ServerSide data source really is installed
   * }
   * ```
   * @since 3.4.0
   */
  protected queryBoolean(type: string, context: unknown = null): boolean {
    const responses = this.grid?.query?.(type, context);
    // Tolerate hosts/tests that hand back a bare value instead of the array.
    return Array.isArray(responses) ? responses.some(Boolean) : !!responses;
  }

  /**
   * Request a re-render of the grid.
   * Uses ROWS phase - does NOT trigger processColumns hooks.
   */
  protected requestRender(): void {
    this.grid?.requestRender?.();
  }

  /**
   * Request a columns re-render of the grid.
   * Uses COLUMNS phase - triggers processColumns hooks.
   * Use this when your plugin needs to reprocess column configuration.
   */
  protected requestColumnsRender(): void {
    (this.grid as { requestColumnsRender?: () => void })?.requestColumnsRender?.();
  }

  /**
   * Request a re-render and restore focus styling afterward.
   * Use this when a plugin action (like expand/collapse) triggers a render
   * but needs to maintain keyboard navigation focus.
   */
  protected requestRenderWithFocus(): void {
    this.grid?.requestRenderWithFocus?.();
  }

  /**
   * Request a lightweight style update without rebuilding DOM.
   * Use this instead of requestRender() when only CSS classes need updating.
   */
  protected requestAfterRender(): void {
    this.grid?.requestAfterRender?.();
  }

  /**
   * Re-render visible rows without rebuilding the row model or recalculating geometry.
   * Use this when row data has been updated in-place (e.g., server-side block loads)
   * and only the visible viewport needs to re-render.
   */
  protected requestVirtualRefresh(): void {
    this.grid?.requestVirtualRefresh?.();
  }

  /**
   * Get the current rows from the grid.
   */
  protected get rows(): any[] {
    return this.grid?.rows ?? [];
  }

  /**
   * Get the original unfiltered/unprocessed rows from the grid.
   * Use this when you need all source data regardless of active filters.
   */
  protected get sourceRows(): any[] {
    return this.grid?.sourceRows ?? [];
  }

  /**
   * Get the current columns from the grid.
   */
  protected get columns(): ColumnConfig[] {
    return this.grid?.columns ?? [];
  }

  /**
   * Get only visible columns from the grid (excludes hidden).
   * Use this for rendering that needs to match the grid template.
   */
  protected get visibleColumns(): ColumnConfig[] {
    return this.grid?._visibleColumns ?? [];
  }

  /**
   * Get the grid as an HTMLElement for direct DOM operations.
   * Use sparingly - prefer the typed GridElementRef API when possible.
   *
   * @example
   * ```ts
   * const width = this.gridElement.clientWidth;
   * this.gridElement.classList.add('my-plugin-active');
   * ```
   */
  protected get gridElement(): HTMLElement {
    return this.grid?._hostElement;
  }

  /**
   * Get the disconnect signal for event listener cleanup.
   * This signal is aborted when the grid disconnects from the DOM.
   * Use this when adding event listeners that should be cleaned up automatically.
   *
   * Best for:
   * - Document/window-level listeners added in attach()
   * - Listeners on the grid element itself
   * - Any listener that should persist across renders
   *
   * Not needed for:
   * - Listeners on elements created in afterRender() (removed with element)
   *
   * @example
   * element.addEventListener('click', handler, { signal: this.disconnectSignal });
   * document.addEventListener('keydown', handler, { signal: this.disconnectSignal });
   */
  protected get disconnectSignal(): AbortSignal {
    // Return the plugin's own abort signal for proper cleanup on detach/re-attach
    // Falls back to grid's signal if plugin's controller not yet created
    return this.#abortController?.signal ?? this.grid?.disconnectSignal;
  }

  /**
   * Get the grid-level icons configuration.
   * Returns merged icons (user config + defaults).
   */
  protected get gridIcons(): Required<GridIcons> {
    const userIcons = this.grid?.gridConfig?.icons ?? {};
    return { ...DEFAULT_GRID_ICONS, ...userIcons };
  }

  /**
   * Resolve a user-visible string from {@link GridConfig.locale}, falling back to
   * the English default supplied at the call site.
   *
   * The fallback is passed inline rather than read from a shipped default map so
   * that a plugin's strings live in the plugin's own chunk.
   *
   * @param key - Namespaced message id, e.g. `'filter.apply'`.
   * @param fallback - English text used when the key is not overridden.
   * @since 3.5.0
   *
   * @example
   * ```ts
   * applyBtn.textContent = this.t('filter.apply', 'Apply');
   * ```
   */
  protected t(key: string, fallback: string): string {
    return this.grid?.effectiveConfig?.locale?.[key] ?? fallback;
  }

  /**
   * A bound `t()` suitable for handing to standalone renderer
   * helpers and public renderer callbacks.
   * @since 3.5.0
   */
  protected get translate(): Translate {
    return (key, fallback) => this.t(key, fallback);
  }

  // #region Animation Helpers

  /**
   * Check if animations are enabled at the grid level.
   * Respects gridConfig.animation.mode and the CSS variable set by the grid.
   *
   * Plugins should use this to skip animations when:
   * - Animation mode is 'off' or `false`
   * - User prefers reduced motion and mode is 'reduced-motion' (default)
   *
   * @example
   * ```ts
   * private get animationStyle(): 'slide' | 'fade' | false {
   *   if (!this.isAnimationEnabled) return false;
   *   return this.config.animation ?? 'slide';
   * }
   * ```
   */
  protected get isAnimationEnabled(): boolean {
    const mode = this.grid?.effectiveConfig?.animation?.mode ?? 'reduced-motion';

    // Explicit off = disabled
    if (mode === false || mode === 'off') return false;

    // Explicit on = always enabled
    if (mode === true || mode === 'on') return true;

    // reduced-motion: check CSS variable (set by grid based on media query)
    const host = this.gridElement;
    if (host) {
      const enabled = getComputedStyle(host).getPropertyValue('--tbw-animation-enabled').trim();
      return enabled !== '0';
    }

    return true; // Default to enabled
  }

  /**
   * Get the animation duration in milliseconds from CSS variable.
   * Falls back to 200ms if not set.
   *
   * Plugins can use this for their animation timing to stay consistent
   * with the grid-level animation.duration setting.
   *
   * @example
   * ```ts
   * element.animate(keyframes, { duration: this.animationDuration });
   * ```
   */
  protected get animationDuration(): number {
    const host = this.gridElement;
    if (host) {
      const durationStr = getComputedStyle(host).getPropertyValue('--tbw-animation-duration').trim();
      const parsed = parseInt(durationStr, 10);
      if (!isNaN(parsed)) return parsed;
    }
    return 200; // Default
  }

  // #endregion

  /**
   * Set an icon on an element using the CSS-first hybrid approach.
   *
   * Sets `data-icon` for CSS pseudo-element rendering and `data-expanded` for
   * expand/collapse toggle elements. Only injects DOM content when a JS override
   * is present (via `gridConfig.icons` or `pluginOverride`), which naturally
   * suppresses the CSS `::before` via the `:empty` selector.
   *
   * @param element - The element to set the icon on
   * @param iconKey - The icon key in GridIcons (e.g., 'expand', 'collapse')
   * @param pluginOverride - Optional plugin-level override (string or HTMLElement)
   */
  protected setIcon(element: HTMLElement, iconKey: keyof GridIcons, pluginOverride?: IconValue): void {
    // Set data-icon attribute for CSS targeting (always)
    element.dataset.icon = toIconAttr(iconKey);

    // Set data-expanded for toggle elements (tree, group, master-detail)
    if (iconKey === 'collapse') {
      element.dataset.expanded = '';
    } else if (iconKey === 'expand') {
      delete element.dataset.expanded;
    }

    // Resolve JS-only icon (returns undefined when CSS default should be used)
    const jsIcon = this.#resolveJsIcon(iconKey, pluginOverride);

    if (jsIcon !== undefined) {
      // JS override: inject content (suppresses CSS ::before via :empty)
      if (typeof jsIcon === 'string') {
        setSanitizedHTML(element, jsIcon);
      } else if (jsIcon instanceof HTMLElement) {
        element.replaceChildren(jsIcon.cloneNode(true));
      }
    } else {
      // CSS path: ensure element is empty so ::before renders
      element.innerHTML = '';
    }
  }

  /**
   * Resolve JS-only icon override. Returns undefined if CSS default should render.
   * Only returns a value when the user explicitly set gridConfig.icons[key] or
   * the plugin passed an override.
   */
  #resolveJsIcon(iconKey: keyof GridIcons, pluginOverride?: IconValue): IconValue | undefined {
    if (pluginOverride !== undefined) return pluginOverride;
    return this.grid?.gridConfig?.icons?.[iconKey];
  }

  /**
   * Create or replace a sort indicator on a header cell.
   *
   * Handles the full lifecycle: removes any existing indicator, creates a new
   * `<span part="sort-indicator" class="sort-indicator">` with the appropriate
   * icon, sets `aria-sort` / `data-sort` attributes, and inserts it before the
   * filter button or resize handle.
   *
   * Plugins decide **which** cells and **when** — this method handles **how**.
   *
   * @param cell - The header cell element
   * @param direction - `'asc'` | `'desc'` for sorted, `null` for unsorted idle state
   * @returns The created indicator element (useful for appending badges, etc.)
   */
  protected updateSortIndicator(cell: Element, direction: 'asc' | 'desc' | null): HTMLElement {
    // Remove any existing indicator (core uses part, plugins use class)
    cell.querySelector('[part~="sort-indicator"], .sort-indicator')?.remove();

    const indicator = document.createElement('span');
    indicator.setAttribute('part', 'sort-indicator');
    indicator.className = 'sort-indicator';

    if (direction) {
      cell.setAttribute('aria-sort', direction === 'asc' ? 'ascending' : 'descending');
      cell.setAttribute('data-sort', direction);
      this.setIcon(indicator, direction === 'asc' ? 'sortAsc' : 'sortDesc');
    } else {
      cell.setAttribute('aria-sort', 'none');
      cell.removeAttribute('data-sort');
      this.setIcon(indicator, 'sortNone');
    }

    // Insert before filter button or resize handle to maintain DOM order
    const insertBefore = cell.querySelector('.tbw-filter-btn') ?? cell.querySelector('.resize-handle');
    if (insertBefore) {
      cell.insertBefore(indicator, insertBefore);
    } else {
      cell.appendChild(indicator);
    }

    return indicator;
  }

  /**
   * Log a warning with an optional diagnostic code.
   *
   * When a diagnostic code is provided, the message is formatted with the code
   * and a link to the troubleshooting docs.
   *
   * @example
   * ```ts
   * this.warn('Something went wrong');                          // plain
   * this.warn(MISSING_BREAKPOINT, 'Set a breakpoint'); // with code + docs link
   * ```
   */
  protected warn(message: string): void;
  protected warn(code: DiagnosticCode, message: string): void;
  protected warn(codeOrMessage: DiagnosticCode | string, message?: string): void {
    if (message !== undefined) {
      // Called with (code, message)
      console.warn(formatDiagnostic(codeOrMessage as DiagnosticCode, message, this.gridElement.id, this.name));
    } else {
      // Called with (message) — plain warning, no diagnostic code
      console.warn(`${gridPrefix(this.gridElement.id, this.name)} ${codeOrMessage}`);
    }
  }

  /**
   * Throw an error with a diagnostic code and docs link.
   * Use for configuration errors and API misuse that should halt execution.
   */
  protected throwDiagnostic(code: DiagnosticCode, message: string): never {
    throw new Error(formatDiagnostic(code, message, this.gridElement.id, this.name));
  }

  // #region Lifecycle Hooks

  /**
   * Transform rows before rendering.
   * Called during each render cycle before rows are rendered to the DOM.
   * Use this to filter, sort, or add computed properties to rows.
   *
   * @param rows - The current rows array (readonly to encourage returning a new array)
   * @returns The modified rows array to render
   *
   * @example
   * ```ts
   * processRows(rows: readonly any[]): any[] {
   *   // Filter out hidden rows
   *   return rows.filter(row => !row._hidden);
   * }
   * ```
   *
   * @example
   * ```ts
   * processRows(rows: readonly any[]): any[] {
   *   // Add computed properties
   *   return rows.map(row => ({
   *     ...row,
   *     _fullName: `${row.firstName} ${row.lastName}`
   *   }));
   * }
   * ```
   */
  processRows?(rows: readonly any[]): any[];

  /**
   * Contribute to the grid's effective configuration during each config merge.
   * Called by {@link ConfigManager.merge} after the base config is assembled,
   * before it becomes `effectiveConfig`. Mutate `config` in place to inject
   * plugin-derived settings (e.g. the shell plugin folds its light-DOM /
   * API-registered state into `config.shell`).
   *
   * This is the config-pipeline counterpart to {@link processColumns} /
   * {@link processRows}: it keeps the core config merge free of any plugin
   * knowledge — each plugin owns the shape it contributes.
   *
   * @param config - The mutable base config being assembled into effectiveConfig.
   *
   * @example
   * ```ts
   * processConfig(config: GridConfig): void {
   *   config.shell = { ...config.shell, header: { title: this.#title } };
   * }
   * ```
   */
  processConfig?(config: GridConfig): void;

  /**
   * Transform columns before rendering.
   * Called during each render cycle before column headers and cells are rendered.
   * Use this to add, remove, or modify column definitions.
   *
   * @param columns - The current columns array (readonly to encourage returning a new array)
   * @returns The modified columns array to render
   *
   * @example
   * ```ts
   * processColumns(columns: readonly ColumnConfig[]): ColumnConfig[] {
   *   // Add a selection checkbox column
   *   return [
   *     { field: '_select', header: '', width: 40 },
   *     ...columns
   *   ];
   * }
   * ```
   */
  processColumns?(columns: readonly ColumnConfig[]): ColumnConfig[];

  /**
   * Reorder the FULL column list — hidden columns included — before `processColumns` runs.
   * Must return a permutation of `columns` (same objects, no additions or removals).
   *
   * A plugin that moves columns (e.g. pinning to an edge) must do it here rather than in
   * `processColumns`, which only ever sees visible columns: core cannot tell from a
   * visible-only result where a hidden column belongs, so it would be misplaced once shown.
   *
   * @param columns - All columns in current model order
   * @returns The reordered columns
   * @internal
   */
  orderColumns?(columns: readonly ColumnConfig[]): ColumnConfig[];

  /**
   * Called before each render cycle begins.
   * Use this to prepare state or cache values needed during rendering.
   *
   * **Note:** This hook is currently a placeholder for future implementation.
   * It is defined in the interface but not yet invoked by the grid's render pipeline.
   * If you need this functionality, please open an issue or contribute an implementation.
   *
   * @example
   * ```ts
   * beforeRender(): void {
   *   this.visibleRowCount = this.calculateVisibleRows();
   * }
   * ```
   */
  beforeRender?(): void;

  /**
   * Called after each render cycle completes.
   * Use this for DOM manipulation, adding event listeners to rendered elements,
   * or applying visual effects like selection highlights.
   *
   * @example
   * ```ts
   * afterRender(): void {
   *   // Apply selection styling to rendered rows
   *   const rows = this.gridElement?.querySelectorAll('.data-grid-row');
   *   rows?.forEach((row, i) => {
   *     row.classList.toggle('selected', this.selectedRows.has(i));
   *   });
   * }
   * ```
   */
  afterRender?(): void;

  /**
   * Called synchronously immediately after the grid (re)builds its root DOM
   * structure, before the browser has a chance to paint.
   *
   * Unlike {@link afterRender} (which runs in the scheduler's STYLE phase and
   * may be deferred), this hook fires inside the same task as the structural
   * DOM build. It is the correct place for plugins that need to wrap, relocate,
   * or augment the freshly built grid DOM without a flash of unstyled/unwrapped
   * content — e.g. a shell that moves the grid content into a chrome wrapper.
   *
   * The grid emits this hook every time it performs a full structural rebuild
   * (initial connect and structural changes), but NOT on the scroll/data hot
   * path. Implementations MUST be idempotent: a full rebuild discards any DOM a
   * previous invocation produced, so re-create it, and no-op when the desired
   * structure is already present.
   *
   * @example
   * ```ts
   * afterStructuralRender(): void {
   *   const root = this.grid?._renderRoot;
   *   if (!root || root.querySelector('.my-wrapper > .tbw-grid-content')) return;
   *   // build wrapper, move .tbw-grid-content inside it
   * }
   * ```
   */
  afterStructuralRender?(): void;

  /**
   * Called synchronously by the grid immediately before it tears down and
   * rebuilds its root DOM structure (a structural re-render). Plugins that
   * relocated existing light-DOM nodes into their own chrome (e.g. a shell that
   * moves toolbar buttons into the header bar) use this to move those nodes back
   * to their original container so they survive the rebuild. Generic
   * counterpart to {@link afterStructuralRender}. MUST be idempotent.
   */
  beforeStructuralRender?(): void;

  /**
   * Called by the grid after a config merge to ask whether the change requires a
   * full structural re-render (rather than a cheaper in-place update). A plugin
   * returns `true` when its rendered DOM signature no longer matches the new
   * effective config — e.g. a shell header appearing/disappearing or a tool
   * panel's count/position/mode changing. The grid performs a structural
   * re-render if ANY plugin returns `true`. MUST be side-effect free.
   */
  needsStructuralRerender?(): boolean;

  /**
   * Called by the grid to let a plugin (re-)parse its own light-DOM
   * contributions into plugin state ahead of a config merge. Generic seam that
   * keeps core free of plugin-specific light-DOM tag knowledge (the plugin reads
   * `this.grid._hostElement`). MUST be idempotent.
   */
  parseLightDom?(): void;

  /**
   * Called by the grid (from `refreshColumns` and the light-DOM observer) to
   * let a plugin re-parse its light-DOM contributions AND refresh any
   * already-rendered chrome in place when something changed — e.g. a shell
   * title or tool buttons that a framework projected asynchronously after the
   * initial render. Distinct from {@link parseLightDom}, which only updates
   * plugin state. Generic seam — core holds no plugin-specific knowledge. MUST
   * be idempotent and a no-op when the plugin's chrome is not yet rendered.
   */
  syncLightDom?(): void;

  /**
   * Called after each cell is rendered.
   * This hook is more efficient than `afterRender()` for cell-level modifications
   * because you receive the cell context directly - no DOM queries needed.
   *
   * Use cases:
   * - Adding selection/highlight classes to specific cells
   * - Injecting badges or decorations
   * - Applying conditional styling based on cell value
   *
   * Performance note: Called for every visible cell during render. Keep implementation fast.
   * This hook is also called during scroll when cells are recycled.
   *
   * @param context - The cell render context with row, column, value, and elements
   *
   * @example
   * ```ts
   * afterCellRender(context: AfterCellRenderContext): void {
   *   // Add selection class without DOM queries
   *   if (this.isSelected(context.rowIndex, context.colIndex)) {
   *     context.cellElement.classList.add('selected');
   *   }
   *
   *   // Add validation error styling
   *   if (this.hasError(context.row, context.column.field)) {
   *     context.cellElement.classList.add('has-error');
   *   }
   * }
   * ```
   */
  afterCellRender?(context: AfterCellRenderContext): void;

  /**
   * Called after a row is fully rendered (all cells complete).
   * Use this for row-level decorations, styling, or ARIA attributes.
   *
   * Common use cases:
   * - Adding selection classes to entire rows (row-focus, selected)
   * - Setting row-level ARIA attributes
   * - Applying row validation highlighting
   * - Tree indentation styling
   *
   * Performance note: Called for every visible row during render. Keep implementation fast.
   * This hook is also called during scroll when rows are recycled.
   *
   * @param context - The row render context with row data and element
   *
   * @example
   * ```ts
   * afterRowRender(context: AfterRowRenderContext): void {
   *   // Add row selection class without DOM queries
   *   if (this.isRowSelected(context.rowIndex)) {
   *     context.rowElement.classList.add('selected', 'row-focus');
   *   }
   *
   *   // Add validation error styling
   *   if (this.rowHasErrors(context.row)) {
   *     context.rowElement.classList.add('has-errors');
   *   }
   * }
   * ```
   */
  afterRowRender?(context: AfterRowRenderContext): void;

  /**
   * Called after scroll-triggered row rendering completes.
   * This is a lightweight hook for applying visual state to recycled DOM elements.
   * Use this instead of afterRender when you need to reapply styling during scroll.
   *
   * Performance note: This is called frequently during scroll. Keep implementation fast.
   *
   * @example
   * ```ts
   * onScrollRender(): void {
   *   // Reapply selection state to visible cells
   *   this.applySelectionToVisibleCells();
   * }
   * ```
   */
  onScrollRender?(): void;

  /**
   * Get the height of a specific row.
   * Used for synthetic rows (group headers, detail panels, etc.) that have fixed heights.
   * Return undefined if this plugin does not manage the height for this row.
   *
   * This hook is called during position cache rebuilds for variable row height virtualization.
   * Plugins that create synthetic rows should implement this to provide accurate heights.
   *
   * @param row - The row data
   * @param index - The row index in the processed rows array
   * @returns The row height in pixels, or undefined if not managed by this plugin
   */
  getRowHeight?(row: unknown, index: number): number | undefined;

  /**
   * Adjust the virtualization start index to render additional rows before the visible range.
   * Use this when expanded content (like detail rows) needs its parent row to remain rendered
   * even when the parent row itself has scrolled above the viewport.
   *
   * @param start - The calculated start row index
   * @param scrollTop - The current scroll position
   * @param rowHeight - The height of a single row
   * @returns The adjusted start index (lower than or equal to original start)
   *
   * @example
   * ```ts
   * adjustVirtualStart(start: number, scrollTop: number, rowHeight: number): number {
   *   // If row 5 is expanded and scrolled partially, keep it rendered
   *   for (const expandedRowIndex of this.expandedRowIndices) {
   *     const expandedRowTop = expandedRowIndex * rowHeight;
   *     const expandedRowBottom = expandedRowTop + rowHeight + this.detailHeight;
   *     if (expandedRowBottom > scrollTop && expandedRowIndex < start) {
   *       return expandedRowIndex;
   *     }
   *   }
   *   return start;
   * }
   * ```
   */
  adjustVirtualStart?(start: number, scrollTop: number, rowHeight: number): number;

  /**
   * Render a custom row, bypassing the default row rendering.
   * Use this for special row types like group headers, detail rows, or footers.
   *
   * @param row - The row data object
   * @param rowEl - The row DOM element to render into
   * @param rowIndex - The index of the row in the data array
   * @returns `true` if the plugin handled rendering (prevents default), `false`/`void` for default rendering
   *
   * @example
   * ```ts
   * renderRow(row: any, rowEl: HTMLElement, rowIndex: number): boolean | void {
   *   if (row._isGroupHeader) {
   *     rowEl.innerHTML = `<div class="group-header">${row._groupLabel}</div>`;
   *     return true; // Handled - skip default rendering
   *   }
   *   // Return void to let default rendering proceed
   * }
   * ```
   */
  renderRow?(row: any, rowEl: HTMLElement, rowIndex: number): boolean | void;

  // #endregion

  // #region Inter-Plugin Communication

  /**
   * Handle queries from other plugins or the grid.
   *
   * Queries are declared in `manifest.queries` and dispatched via `grid.query()`.
   * This enables type-safe, discoverable inter-plugin communication.
   *
   * @category Plugin Development
   * @param query - The query object with type and context
   * @returns Query-specific response, or undefined if not handling this query
   *
   * @example
   * ```ts
   * // In manifest
   * static override readonly manifest: PluginManifest = {
   *   queries: [
   *     { type: 'canMoveColumn', description: 'Check if a column can be moved' },
   *   ],
   * };
   *
   * // In plugin class
   * handleQuery(query: PluginQuery): unknown {
   *   if (query.type === 'canMoveColumn') {
   *     const column = query.context as ColumnConfig;
   *     return !column.sticky; // Can't move sticky columns
   *   }
   * }
   * ```
   */
  handleQuery?(query: PluginQuery): unknown;

  // #endregion

  // #region Interaction Hooks

  /**
   * Handle keyboard events on the grid.
   * Called when a key is pressed while the grid or a cell has focus.
   *
   * @param event - The native KeyboardEvent
   * @returns `true` to prevent default behavior and stop propagation, `false`/`void` to allow default
   *
   * @example
   * ```ts
   * onKeyDown(event: KeyboardEvent): boolean | void {
   *   // Handle Ctrl+A for select all
   *   if (event.ctrlKey && event.key === 'a') {
   *     this.selectAllRows();
   *     return true; // Prevent default browser select-all
   *   }
   * }
   * ```
   */
  onKeyDown?(event: KeyboardEvent): boolean | void;

  /**
   * Handle cell click events.
   * Called when a data cell is clicked (not headers).
   *
   * @param event - Cell click event with row/column context
   * @returns `true` to prevent default behavior and stop propagation, `false`/`void` to allow default
   *
   * @example
   * ```ts
   * onCellClick(event: CellClickEvent): boolean | void {
   *   if (event.field === '_select') {
   *     this.toggleRowSelection(event.rowIndex);
   *     return true; // Handled
   *   }
   * }
   * ```
   */
  onCellClick?(event: CellClickEvent): boolean | void;

  /**
   * Handle row click events.
   * Called when any part of a data row is clicked.
   * Note: This is called in addition to onCellClick, not instead of.
   *
   * @param event - Row click event with row context
   * @returns `true` to prevent default behavior and stop propagation, `false`/`void` to allow default
   *
   * @example
   * ```ts
   * onRowClick(event: RowClickEvent): boolean | void {
   *   if (this.config.mode === 'row') {
   *     this.selectRow(event.rowIndex, event.originalEvent);
   *     return true;
   *   }
   * }
   * ```
   */
  onRowClick?(event: RowClickEvent): boolean | void;

  /**
   * Handle header click events.
   * Called when a column header is clicked. Commonly used for sorting.
   *
   * @param event - Header click event with column context
   * @returns `true` to prevent default behavior and stop propagation, `false`/`void` to allow default
   *
   * @example
   * ```ts
   * onHeaderClick(event: HeaderClickEvent): boolean | void {
   *   if (event.column.sortable !== false) {
   *     this.toggleSort(event.field);
   *     return true;
   *   }
   * }
   * ```
   */
  onHeaderClick?(event: HeaderClickEvent): boolean | void;

  /**
   * Handle scroll events on the grid viewport.
   * Called during scrolling. Note: This may be called frequently; debounce if needed.
   *
   * @param event - Scroll event with scroll position and viewport dimensions
   *
   * @example
   * ```ts
   * onScroll(event: ScrollEvent): void {
   *   // Update sticky column positions
   *   this.updateStickyPositions(event.scrollLeft);
   * }
   * ```
   */
  onScroll?(event: ScrollEvent): void;

  /**
   * Handle cell mousedown events.
   * Used for initiating drag operations like range selection or column resize.
   *
   * @param event - Mouse event with cell context
   * @returns `true` to indicate drag started (prevents text selection), `false`/`void` otherwise
   *
   * @example
   * ```ts
   * onCellMouseDown(event: CellMouseEvent): boolean | void {
   *   if (event.rowIndex !== undefined && this.config.mode === 'range') {
   *     this.startDragSelection(event.rowIndex, event.colIndex);
   *     return true; // Prevent text selection
   *   }
   * }
   * ```
   */
  onCellMouseDown?(event: CellMouseEvent): boolean | void;

  /**
   * Handle cell mousemove events during drag operations.
   * Only called when a drag is in progress (after mousedown returned true).
   *
   * @param event - Mouse event with current cell context
   * @returns `true` to continue handling the drag, `false`/`void` otherwise
   *
   * @example
   * ```ts
   * onCellMouseMove(event: CellMouseEvent): boolean | void {
   *   if (this.isDragging && event.rowIndex !== undefined) {
   *     this.extendSelection(event.rowIndex, event.colIndex);
   *     return true;
   *   }
   * }
   * ```
   */
  onCellMouseMove?(event: CellMouseEvent): boolean | void;

  /**
   * Handle cell mouseup events to end drag operations.
   *
   * @param event - Mouse event with final cell context
   * @returns `true` if drag was finalized, `false`/`void` otherwise
   *
   * @example
   * ```ts
   * onCellMouseUp(event: CellMouseEvent): boolean | void {
   *   if (this.isDragging) {
   *     this.finalizeDragSelection();
   *     this.isDragging = false;
   *     return true;
   *   }
   * }
   * ```
   */
  onCellMouseUp?(event: CellMouseEvent): boolean | void;

  // Note: Context menu items are provided via handleQuery('getContextMenuItems').
  // This keeps the core decoupled from the context-menu plugin specifics.

  // #endregion

  // #region Column State Hooks

  /**
   * Contribute plugin-specific state for a column.
   * Called by the grid when collecting column state for serialization.
   * Plugins can add their own properties to the column state.
   *
   * @param field - The field name of the column
   * @returns Partial column state with plugin-specific properties, or undefined if no state to contribute
   *
   * @example
   * ```ts
   * getColumnState(field: string): Partial<ColumnState> | undefined {
   *   const filterModel = this.filterModels.get(field);
   *   if (filterModel) {
   *     // Uses module augmentation to add filter property to ColumnState
   *     return { filter: filterModel } as Partial<ColumnState>;
   *   }
   *   return undefined;
   * }
   * ```
   */
  getColumnState?(field: string): Partial<ColumnState> | undefined;

  /**
   * Apply plugin-specific state to a column.
   * Called by the grid when restoring column state from serialized data.
   * Plugins should restore their internal state based on the provided state.
   *
   * @param field - The field name of the column
   * @param state - The column state to apply (may contain plugin-specific properties)
   *
   * @example
   * ```ts
   * applyColumnState(field: string, state: ColumnState): void {
   *   // Check for filter property added via module augmentation
   *   const filter = (state as any).filter;
   *   if (filter) {
   *     this.filterModels.set(field, filter);
   *     this.applyFilter();
   *   }
   * }
   * ```
   */
  applyColumnState?(field: string, state: ColumnState): void;

  // #endregion

  // #region Scroll Boundary Hooks

  /**
   * Report horizontal scroll boundary offsets for this plugin.
   * Plugins that obscure part of the scroll area (e.g., pinned/sticky columns)
   * should return how much space they occupy on each side.
   * The keyboard navigation uses this to ensure focused cells are fully visible.
   *
   * @param rowEl - The row element (optional, for calculating widths from rendered cells)
   * @param focusedCell - The currently focused cell element (optional, to determine if scrolling should be skipped)
   * @returns Object with left/right pixel offsets and optional skipScroll flag, or undefined if plugin has no offsets
   *
   * @example
   * ```ts
   * getHorizontalScrollOffsets(rowEl?: HTMLElement, focusedCell?: HTMLElement): { left: number; right: number; skipScroll?: boolean } | undefined {
   *   // Calculate total width of left-pinned columns
   *   const leftCells = rowEl?.querySelectorAll('.sticky-left') ?? [];
   *   let left = 0;
   *   leftCells.forEach(el => { left += (el as HTMLElement).offsetWidth; });
   *   // Skip scroll if focused cell is pinned (always visible)
   *   const skipScroll = focusedCell?.classList.contains('sticky-left');
   *   return { left, right: 0, skipScroll };
   * }
   * ```
   */
  getHorizontalScrollOffsets?(
    rowEl?: HTMLElement,
    focusedCell?: HTMLElement,
  ): { left: number; right: number; skipScroll?: boolean } | undefined;

  /**
   * Report vertical scroll boundary offsets for this plugin.
   * Plugins that overlay the top or bottom of the rows viewport (e.g. sticky
   * row clones) should return how much space they obscure on each edge, so
   * keyboard navigation can scroll a focused row clear of the overlay rather
   * than leaving it hidden underneath — WCAG 2.2 SC 2.4.11 Focus Not Obscured.
   *
   * Only report space that is painted *over* the scrollable rows. Content that
   * takes part in normal flow (pinned-row bars, the column header) already
   * shrinks the viewport and must NOT be reported here, or rows would scroll
   * twice as far as needed.
   *
   * @param focusedRowIndex - Index of the row being scrolled into view, so a plugin can report `skipScroll` when it already renders that row in a permanently visible overlay
   * @returns Object with top/bottom pixel offsets and optional skipScroll flag, or undefined if plugin obscures nothing
   *
   * @example
   * ```ts
   * getVerticalScrollOffsets(focusedRowIndex?: number): { top: number; bottom: number; skipScroll?: boolean } | undefined {
   *   if (!this.overlayEl) return undefined;
   *   // The row is already pinned in the overlay — it is visible where it is.
   *   if (focusedRowIndex != null && this.stuckIndices.includes(focusedRowIndex)) {
   *     return { top: 0, bottom: 0, skipScroll: true };
   *   }
   *   return { top: this.overlayEl.offsetHeight, bottom: 0 };
   * }
   * ```
   *
   * @since 3.6.0
   */
  getVerticalScrollOffsets?(
    focusedRowIndex?: number,
  ): { top: number; bottom: number; skipScroll?: boolean } | undefined;

  // #endregion

  // #region Shell Integration Hooks

  /**
   * Register a tool panel for this plugin.
   * Return undefined if plugin has no tool panel.
   * The shell will create a toolbar toggle button and render the panel content
   * when the user opens the panel.
   *
   * @returns Tool panel definition, or undefined if plugin has no panel
   *
   * @example
   * ```ts
   * getToolPanel(): ToolPanelDefinition | undefined {
   *   return {
   *     id: 'columns',
   *     title: 'Columns',
   *     icon: '☰',
   *     tooltip: 'Show/hide columns',
   *     order: 10,
   *     render: (container) => {
   *       this.renderColumnList(container);
   *       return () => this.cleanup();
   *     },
   *   };
   * }
   * ```
   */
  getToolPanel?(): ToolPanelDefinition | undefined;

  /**
   * Register content for the shell header center section.
   * Return undefined if plugin has no header content.
   * Examples: search input, selection summary, status indicators.
   *
   * @returns Header content definition, or undefined if plugin has no header content
   *
   * @example
   * ```ts
   * getHeaderContent(): HeaderContentDefinition | undefined {
   *   return {
   *     id: 'quick-filter',
   *     order: 10,
   *     render: (container) => {
   *       const input = document.createElement('input');
   *       input.type = 'text';
   *       input.placeholder = 'Search...';
   *       input.addEventListener('input', this.handleInput);
   *       container.appendChild(input);
   *       return () => input.removeEventListener('input', this.handleInput);
   *     },
   *   };
   * }
   * ```
   */
  getHeaderContent?(): HeaderContentDefinition | undefined;

  // #endregion
}
